import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ProfileView from './ProfileView';
import socialService, { ProfileDto } from '../../services/socialService';
import { useAuth } from '../../hooks/useAuth';
import {
  isProfileCacheStale,
  readCachedProfile,
  writeCachedProfile,
} from '../../services/profileLocalStore';
import { getProfileViewDeniedMessage } from '../../utils/profileViewAccess';

interface ProfileOverlayDialogProps {
  open: boolean;
  aliasName: string;
  onClose: () => void;
}

const ProfileOverlayDialog: React.FC<ProfileOverlayDialogProps> = ({ open, aliasName, onClose }) => {
  const { user } = useAuth();
  const viewerUsername = (user?.username ?? '').trim();
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState<string | null>(null);
  const [cacheStale, setCacheStale] = useState(false);
  const [cachedAt, setCachedAt] = useState<number | null>(null);

  const loadFromNetwork = useCallback(async (force = false) => {
    if (!aliasName?.trim()) return;
    setLoading(true);
    setDeniedMessage(null);
    try {
      const cached = viewerUsername ? readCachedProfile(viewerUsername, aliasName) : null;
      const summaryResult = await socialService.getProfileSummary(aliasName);

      if (!summaryResult.data?.canViewProfile) {
        setProfile(null);
        setDeniedMessage(getProfileViewDeniedMessage(aliasName, summaryResult.data));
        return;
      }

      const profileResult = await socialService.getProfile(aliasName, {
        ifNoneMatch: !force ? cached?.etag : undefined,
      });

      if (profileResult.notModified && cached) {
        setProfile(cached.profile);
        if (viewerUsername) {
          writeCachedProfile(viewerUsername, aliasName, cached.profile, cached.canEdit, cached.etag);
        }
        setCachedAt(cached.cachedAt);
        setCacheStale(isProfileCacheStale(cached.cachedAt));
        return;
      }

      if (!profileResult.data) {
        throw new Error('Profile unavailable');
      }

      setProfile(profileResult.data.profile);
      if (viewerUsername) {
        writeCachedProfile(
          viewerUsername,
          aliasName,
          profileResult.data.profile,
          profileResult.data.canEdit,
          profileResult.etag,
        );
      }
      setCachedAt(Date.now());
      setCacheStale(false);
    } catch {
      if (!force) {
        setDeniedMessage(getProfileViewDeniedMessage(aliasName, null));
      } else {
        setDeniedMessage('Could not refresh this profile. Try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [aliasName, viewerUsername]);

  useEffect(() => {
    if (!open || !aliasName?.trim()) {
      return;
    }

    setDeniedMessage(null);
    setProfile(null);

    const cached = viewerUsername ? readCachedProfile(viewerUsername, aliasName) : null;
    if (cached) {
      setProfile(cached.profile);
      setCachedAt(cached.cachedAt);
      setCacheStale(isProfileCacheStale(cached.cachedAt));
    } else {
      setCachedAt(null);
      setCacheStale(false);
    }

    void loadFromNetwork();
  }, [open, aliasName, viewerUsername, loadFromNetwork]);

  const handleRefresh = () => {
    void loadFromNetwork(true);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Typography variant="h6" component="span" sx={{ flex: 1 }}>
          Profile — @{aliasName}
        </Typography>
        {profile && (
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
        )}
        <IconButton onClick={onClose} aria-label="Close profile">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {cacheStale && cachedAt && profile && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Cached profile may be out of date (older than 24 hours). Tap Refresh to load the latest.
          </Alert>
        )}
        {loading && !profile && !deniedMessage && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {deniedMessage && !profile && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {deniedMessage}
          </Alert>
        )}
        {profile && (
          <ProfileView profile={profile} isOwner={false} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileOverlayDialog;
